<script setup lang="ts">
/**
 * PEmptyState — état vide d'une liste, avec illustration optionnelle
 * (slot icon), titre, description et CTA (slot action).
 *
 * Pattern Revelio §5.3 — variant `dashed` (border dashed line, fond
 * surface) est le pattern privilégié pour les listes vides où une
 * action de création est attendue.
 *
 * Exemples :
 *   <PEmptyState
 *     title="Aucune API key pour l'instant"
 *     description="Créez votre première clé pour commencer à appeler /v1/messages."
 *   >
 *     <template #action><PButton>+ Nouvelle clé</PButton></template>
 *   </PEmptyState>
 */
interface Props {
    title: string
    description?: string
    /** dashed = border-dashed line, flat = border solide (statut neutre). */
    variant?: 'dashed' | 'flat'
    /** Compact (dans une card) ou page entière (cas par défaut). */
    size?: 'sm' | 'md' | 'lg'
}

withDefaults(defineProps<Props>(), {
    variant: 'dashed',
    size: 'md',
})
</script>

<template>
    <div
        :class="[
            'p-empty',
            `p-empty--${variant}`,
            `p-empty--${size}`,
        ]"
    >
        <div v-if="$slots.icon" class="p-empty__icon" aria-hidden="true">
            <slot name="icon" />
        </div>
        <h3 class="p-empty__title">{{ title }}</h3>
        <p v-if="description" class="p-empty__desc">{{ description }}</p>
        <div v-if="$slots.action" class="p-empty__action">
            <slot name="action" />
        </div>
    </div>
</template>

<style scoped>
.p-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: var(--p-color-surface);
    border-radius: var(--p-radius-sm);
    color: var(--p-color-ink-5);
    transition:
        border-color var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}

.p-empty--dashed {
    border: 1px dashed var(--p-color-line);
}
.p-empty--dashed:hover {
    border-color: var(--p-color-ink-6);
    color: var(--p-color-ink-4);
}

.p-empty--flat {
    border: 1px solid var(--p-color-line);
}

.p-empty--sm {
    padding: 24px 20px;
    gap: 8px;
}
.p-empty--md {
    padding: 48px 32px;
    gap: 12px;
}
.p-empty--lg {
    padding: 80px 40px;
    gap: 16px;
}

.p-empty__icon {
    color: var(--p-color-ink-6);
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.p-empty__title {
    margin: 0;
    font-family: var(--p-font-sans);
    font-size: var(--p-text-sm);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink);
}

.p-empty__desc {
    margin: 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-4);
    line-height: var(--p-line-relaxed);
    max-width: 36ch;
}

.p-empty__action {
    margin-top: 16px;
}
</style>
